// Outbound Caller → Settings → Usage & Billing. Shows the leads plan of the
// selected agent: conversations counted this period and what they cost so far.
// Invoices and the card are managed on the profile page, so those rows go there.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./OutboundBilling.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import PounceFooterNav from "../PounceAgentDetail/PounceFooterNav/PounceFooterNav";
import { getOutboundUsage } from "../../Store/apiStore";
import { currencySymbol } from "../../lib/checkoutCurrency";
import OutboundCallerBrand from "../OutboundCallerBrand/OutboundCallerBrand";

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const InvoiceIcon = () => (
  <svg {...svgProps}>
    <path d="M6 2h9l5 5v15H6z" />
    <path d="M14 2v6h6M9 13h6M9 17h6" />
  </svg>
);

const CardIcon = () => (
  <svg {...svgProps}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>
);

const CalendarIcon = () => (
  <svg {...svgProps}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 11h18" />
  </svg>
);

const InfoIcon = () => (
  <svg {...svgProps}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

const Chevron = () => (
  <svg
    className={styles.chevron}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

// 2 → "2", 2.5 → "2.50"
const amount = (value) =>
  Number.isInteger(value) ? String(value) : Number(value).toFixed(2);

const cardText = (card) =>
  card
    ? `${card.brand.charAt(0).toUpperCase()}${card.brand.slice(1)} •••• ${card.last4}`
    : "Add or update your card";

const OutboundBilling = () => {
  const navigate = useNavigate();
  const agentId = sessionStorage.getItem("SelectAgentId") || "";
  // null = loading, false = failed, otherwise the server's { plan, usage, card }.
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    setData(null);
    getOutboundUsage(agentId)
      .then(setData)
      .catch(() => setData(false));
  }, [agentId]);

  useEffect(load, [load]);

  const { plan, usage, card } = data || {};
  const sym = currencySymbol(plan?.currency);
  const percent = usage?.limit
    ? Math.min(100, Math.round((usage.used / usage.limit) * 100))
    : 0;

  return (
    <div className={styles.page}>
      {/* <div className={styles.brand}>
        <OutboundCallerBrand />
      </div> */}
      {/* <div className={styles.head}>
        <button
          type="button"
          className={styles.back}
          aria-label="Back"
          onClick={() => navigate(-1)}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M15 6l-6 6 6 6"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className={styles.title}>Usage &amp; Billing</h1>
      </div> */}
      {/* App ka common HeaderBar: back arrow + "Outbound Caller" usi size me jo
          baaki screens ke header title ka hai. Alag brand row (call icon wali)
          ki ab zaroorat nahi. */}
      <div className={styles.header}>
        <HeaderBar
          title="Outbound Caller"
          subtitle="Usage & Billing"
          backgroundColor="#fff"
          onBack={() => navigate("/outbound-settings")}
        />
      </div>

      <div className={styles.content}>
        {/* Placeholders in the shape of the cards and rows that replace them. */}
        {data === null && (
          <div role="status" aria-label="Loading usage">
            <div className={`${styles.skeleton} ${styles.skeletonPlan}`} />
            <div className={`${styles.skeleton} ${styles.skeletonUsage}`} />
            <div className={`${styles.skeleton} ${styles.skeletonRow}`} />
            <div className={`${styles.skeleton} ${styles.skeletonRow}`} />
          </div>
        )}

        {data === false && (
          <div className={styles.card}>
            <p className={styles.note}>
              We couldn't load your usage. Please try again.
            </p>
            <button type="button" className={styles.retry} onClick={load}>
              Retry
            </button>
          </div>
        )}

        {data && !plan && (
          <div className={styles.card}>
            <p className={styles.note}>
              Usage details are not available for this agent's plan.
            </p>
          </div>
        )}

        {plan && (
          <>
            <div className={styles.planCard}>
              <div className={styles.planTop}>
                <span className={styles.label}>Current Plan</span>
                {plan.trial && <span className={styles.badge}>Free Trial</span>}
              </div>
              <div className={styles.planName}>{plan.name}</div>
              <div className={styles.planPrice}>
                {sym}
                {amount(plan.monthlyPrice)}
                <span>/month</span>
              </div>
              {plan.trial && (
                <div className={styles.planMeta}>
                  <CalendarIcon />
                  Trial ends {formatDate(plan.trialEndsAt)}
                </div>
              )}
              {!plan.trial && usage.periodEnd && (
                <div className={styles.planMeta}>
                  <CalendarIcon />
                  {plan.cancelAtPeriodEnd ? "Ends" : "Renews"}{" "}
                  {formatDate(usage.periodEnd)}
                </div>
              )}
            </div>

            <div className={styles.card}>
              <div className={styles.cardHead}>
                <div>
                  <div className={styles.cardTitle}>Usage This Period</div>
                  {usage.periodStart && (
                    <div className={styles.period}>
                      {formatDate(usage.periodStart)} –{" "}
                      {formatDate(usage.periodEnd)}
                    </div>
                  )}
                </div>
                {!!usage.limit && (
                  <span className={styles.percent}>{percent}%</span>
                )}
              </div>

              <div className={styles.usedRow}>
                <span>Conversations Used</span>
                <strong>
                  {usage.used}
                  {!!usage.limit && <small> / {usage.limit}</small>}
                </strong>
              </div>
              {!!usage.limit && (
                <div
                  className={styles.bar}
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                  aria-label="Conversations used"
                >
                  <span style={{ width: `${percent}%` }} />
                </div>
              )}

              <div className={styles.tiles}>
                <div className={styles.tile}>
                  <span>Additional</span>
                  <strong>{usage.additional}</strong>
                </div>
                <div className={styles.tile}>
                  <span>Estimated Charge</span>
                  <strong>
                    {sym}
                    {Number(usage.estimatedCharge).toFixed(2)}
                  </strong>
                </div>
              </div>

              {plan.extraLeadPrice !== null && (
                <p className={styles.hint}>
                  <InfoIcon />
                  <span>
                    {sym}
                    {amount(plan.extraLeadPrice)} per additional New Unique Lead
                    Conversation
                  </span>
                </p>
              )}
            </div>
          </>
        )}

        {data !== null && (
          <div className={styles.list}>
            <button
              type="button"
              className={styles.row}
              onClick={() => navigate("/outbound-invoices")}
            >
              <span className={styles.icon} aria-hidden="true">
                <InvoiceIcon />
              </span>
              <span className={styles.text}>
                <span className={styles.rowTitle}>Invoices</span>
                <span className={styles.rowDesc}>
                  View and download past invoices
                </span>
              </span>
              <Chevron />
            </button>
            {/* The hash makes the profile page scroll to its card section. */}
            <button
              type="button"
              className={styles.row}
              onClick={() => navigate("/edit-profile#payment-method")}
            >
              <span className={styles.icon} aria-hidden="true">
                <CardIcon />
              </span>
              <span className={styles.text}>
                <span className={styles.rowTitle}>Payment Method</span>
                <span className={styles.rowDesc}>{cardText(card)}</span>
              </span>
              <Chevron />
            </button>
          </div>
        )}
      </div>

      <PounceFooterNav agentId={agentId} active="settings" />
    </div>
  );
};

export default OutboundBilling;
